import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { versionLabel, type CorpusItem, type CorpusItemSummary, type Paginated } from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Dialog,
  Field,
  Input,
  LoadingPane,
  PageHeader,
  Select,
  Table,
  Textarea,
  formatDate,
  useToast,
} from '../../components/ui.js';

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

export function CorpusPage() {
  const { org } = useOrg();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canWrite = org.role === 'admin' || org.role === 'contributor';

  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const corpus = useQuery({
    queryKey: ['corpus', org.orgId, status],
    queryFn: () =>
      apiFetch<Paginated<CorpusItemSummary>>(`/orgs/${org.orgId}/corpus`, {
        query: { limit: 200, status: status || undefined },
      }),
  });

  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [key, setKey] = useState('');
  const [keyTouched, setKeyTouched] = useState(false);
  const [description, setDescription] = useState('');
  const create = useMutation({
    mutationFn: () =>
      apiFetch<CorpusItem>(`/orgs/${org.orgId}/corpus`, {
        method: 'POST',
        body: { key: key || slugify(title), title: title.trim(), description: description.trim() },
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['corpus', org.orgId] });
      toast('Corpus item created.', 'success');
      navigate(`/corpus/${created.id}`);
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not create the item', 'error'),
  });

  const filtered = useMemo(() => {
    const items = corpus.data?.items ?? [];
    if (!search.trim()) return items;
    const needle = search.toLowerCase();
    return items.filter(
      (i) => i.title.toLowerCase().includes(needle) || i.key.includes(needle),
    );
  }, [corpus.data, search]);

  if (corpus.isLoading) return <LoadingPane label="Loading corpus" />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (title.trim()) create.mutate();
  };

  return (
    <div>
      <PageHeader
        title="Shared corpus"
        subtitle="Your organisation's reusable compliance content — write once, map to every standard"
        actions={canWrite && <Button onClick={() => setOpen(true)}>New item</Button>}
      />

      <div className="filter-bar" style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
        <Input
          aria-label="Search corpus"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 280 }}
        />
        <Select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          style={{ width: 'auto' }}
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
          <option value="archived">Archived</option>
        </Select>
      </div>

      <Table
        head={
          <>
            <th>Title</th>
            <th>Key</th>
            <th>Status</th>
            <th>Version</th>
            <th>Updated</th>
          </>
        }
      >
        {filtered.map((item) => (
          <tr key={item.id}>
            <td>
              <Link to={`/corpus/${item.id}`}>{item.title}</Link>
              {item.description && (
                <div style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)' }}>
                  {item.description}
                </div>
              )}
            </td>
            <td>
              <Badge>{item.key}</Badge>
            </td>
            <td>
              <Badge tone={item.status === 'approved' ? 'success' : item.status === 'archived' ? 'neutral' : 'info'}>
                {item.status}
              </Badge>
            </td>
            <td>{item.currentVersion > 0 ? versionLabel(item.documentVersion, item.currentVersion) : '—'}</td>
            <td style={{ whiteSpace: 'nowrap' }}>{formatDate(item.updatedAt)}</td>
          </tr>
        ))}
      </Table>

      <Dialog open={open} onClose={() => setOpen(false)} title="New corpus item">
        <form onSubmit={submit}>
          <Field label="Title" required>
            {(props) => (
              <Input
                {...props}
                required
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (!keyTouched) setKey(slugify(e.target.value));
                }}
              />
            )}
          </Field>
          <Field label="Key" hint="Stable identifier used for crosswalk mappings and pre-fill">
            {(props) => (
              <Input
                {...props}
                value={key}
                pattern="[a-z0-9][a-z0-9-]*"
                onChange={(e) => {
                  setKeyTouched(true);
                  setKey(e.target.value.toLowerCase());
                }}
              />
            )}
          </Field>
          <Field label="Description" hint="Optional">
            {(props) => (
              <Textarea {...props} value={description} onChange={(e) => setDescription(e.target.value)} />
            )}
          </Field>
          <div className="dialog-actions">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending}>
              Create
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
